// A21 — Shadow Teams + Real-Work Evaluation. Lists shadow runs +
// recipe scorecards + cross-recipe comparison view. v1 surface is
// terse since the data flows in only after recipe_scorecard_pass +
// receipts have generated content; the UI gracefully handles empty
// state.

import { useEffect, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import {
  ApiError,
  comparisonMetrics,
  createShadowRun,
  listRecipeScorecards,
  listShadowRuns,
  type ComparisonRow,
  type RecipeScorecardRow,
  type ShadowRunRow,
} from "../api/client";
import { tables } from "../../spacetimedb-bindings";
import { UpsellCard, isPremiumRequired } from "./UpsellCard";

interface Props {
  workspaceId: number;
}

function bigintToNumber(v: unknown): number {
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "number") return v;
  return 0;
}

export function ShadowRunsPanel({ workspaceId }: Props) {
  const [agendaRows] = useTable(tables.agenda);
  const [runs, setRuns] = useState<ShadowRunRow[]>([]);
  const [scorecards, setScorecards] = useState<RecipeScorecardRow[]>([]);
  const [comparison, setComparison] = useState<ComparisonRow[]>([]);
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [premiumGated, setPremiumGated] = useState(false);

  const agendas = useMemo(
    () =>
      agendaRows
        .filter((row) => bigintToNumber(row.workspaceId) === workspaceId)
        .map((row) => ({
          agenda_id: bigintToNumber(row.agendaId),
          label: row.title,
          order_index: row.orderIndex,
        }))
        .sort((a, b) => a.order_index - b.order_index),
    [agendaRows, workspaceId],
  );

  const refresh = async () => {
    setBusy(true);
    setError(null);
    try {
      const [r, s, c] = await Promise.all([
        listShadowRuns(workspaceId),
        listRecipeScorecards(workspaceId),
        comparisonMetrics(workspaceId, tag.trim() || undefined),
      ]);
      setRuns(r);
      setScorecards(s);
      setComparison(c);
      setPremiumGated(false);
    } catch (e) {
      if (isPremiumRequired(e)) {
        setPremiumGated(true);
      } else {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-edge bg-surface-raised p-3"
      data-testid="shadow-runs-panel"
    >
      <header>
        <h3 className="text-xs font-semibold text-primary">
          Shadow Teams + comparison
        </h3>
        <p className="mt-1 text-[0.625rem] text-muted">
          Counterfactual team trials against the same tagged work.
          Comparison reads from `recipe_scorecard` so every claim
          ("cheaper", "faster") has a receipt count behind it.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          placeholder="filter by task_tag (blank = all)"
          className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
        />
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={busy}
          className="rounded-md border border-edge px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
        >
          {busy ? "Loading…" : "Refresh"}
        </button>
        {error && <span className="text-[0.625rem] text-danger">{error}</span>}
      </div>

      <section className="flex flex-col gap-1">
        <h4 className="text-[0.625rem] font-medium text-muted">Comparison</h4>
        {comparison.length === 0 ? (
          <p className="text-[0.625rem] text-faint">
            No scorecards match yet. Run real work, attach task_tags, and
            recipe_scorecard_pass will populate this hourly.
          </p>
        ) : (
          <table className="w-full text-[0.625rem]">
            <thead className="text-faint">
              <tr>
                <th className="text-left">recipe</th>
                <th className="text-left">tag</th>
                <th className="text-right">avg cost</th>
                <th className="text-right">accept %</th>
                <th className="text-right">CI %</th>
                <th className="text-right">corrections %</th>
                <th className="text-right">n</th>
              </tr>
            </thead>
            <tbody className="font-code">
              {comparison.map((c) => (
                <tr
                  key={`${c.recipe_id}-${c.task_tag}`}
                  data-testid={`comparison-${c.recipe_id}-${c.task_tag}`}
                >
                  <td>{c.recipe_id}</td>
                  <td>{c.task_tag}</td>
                  <td className="text-right">${(c.avg_cost_micros / 1_000_000).toFixed(3)}</td>
                  <td className="text-right">{Math.round(c.acceptance_rate * 100)}%</td>
                  <td className="text-right">{Math.round(c.ci_pass_rate * 100)}%</td>
                  <td className="text-right">{Math.round(c.correction_rate * 100)}%</td>
                  <td className="text-right">{c.receipt_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="flex flex-col gap-1">
        <h4 className="text-[0.625rem] font-medium text-muted">Shadow runs</h4>
        {runs.length === 0 ? (
          <p className="text-[0.625rem] text-faint">No shadow runs yet.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {runs.map((r) => (
              <li
                key={r.shadow_run_id}
                className="rounded-md border border-edge bg-surface px-2 py-1"
                data-testid={`shadow-run-${r.shadow_run_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-code text-[0.625rem]">
                    #{r.shadow_run_id} · {r.harness}
                  </span>
                  <span
                    className={`font-code text-[0.625rem] ${
                      r.status === "complete"
                        ? "text-success"
                        : r.status === "failed"
                          ? "text-danger"
                          : "text-warning"
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
                <p className="mt-0.5 text-[0.625rem] text-muted">
                  source: {r.source_kind} #{r.source_id} · spent $
                  {(r.spent_micros / 1_000_000).toFixed(3)} of $
                  {(r.budget_micros / 1_000_000).toFixed(3)}
                </p>
                {r.error && (
                  <p className="text-[0.625rem] text-danger">{r.error}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-1">
        <h4 className="text-[0.625rem] font-medium text-muted">Recipe scorecards</h4>
        {scorecards.length === 0 ? (
          <p className="text-[0.625rem] text-faint">
            No scorecards yet — recipe_scorecard_pass aggregates these
            hourly from receipts + outcome_tags.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-1 md:grid-cols-2">
            {scorecards.slice(0, 20).map((s) => (
              <li
                key={s.scorecard_id}
                className="rounded-md border border-edge bg-surface px-2 py-1"
                data-testid={`scorecard-${s.scorecard_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-code text-[0.625rem]">
                    recipe {s.recipe_id} · {s.task_tag}
                  </span>
                  <span className="font-code text-[0.625rem] text-faint">
                    n={s.receipt_count}
                  </span>
                </div>
                <p className="mt-0.5 text-[0.625rem] text-muted">
                  ${(s.avg_cost_micros / 1_000_000).toFixed(3)} avg · accept{" "}
                  {Math.round(s.acceptance_rate * 100)}% · CI{" "}
                  {Math.round(s.ci_pass_rate * 100)}%
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {premiumGated ? (
        <UpsellCard
          feature="shadow-teams"
          headline="Shadow Teams are a premium feature"
          sub="Alternate-build dispatch — run counterfactual team trials against the same tagged work — unlocks with premium."
        />
      ) : (
        <DemoCreateRow
          agendas={agendas}
          workspaceId={workspaceId}
          onCreated={() => void refresh()}
          onPremiumRequired={() => setPremiumGated(true)}
        />
      )}
    </section>
  );
}

function DemoCreateRow({
  agendas,
  workspaceId,
  onCreated,
  onPremiumRequired,
}: {
  agendas: Array<{ agenda_id: number; label: string }>;
  workspaceId: number;
  onCreated: () => void;
  onPremiumRequired: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [agendaId, setAgendaId] = useState("");
  const [budget, setBudget] = useState("0.50");

  const submit = async () => {
    const aid = Number(agendaId);
    const usd = Number(budget);
    if (!Number.isFinite(aid) || aid <= 0) {
      setErr("agenda_id required");
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      void import("../observability/posthog").then((m) =>
        m.capture("shadow_run_queued", {
          workspace_id: workspaceId,
          agenda_id: aid,
          budget_usd: usd,
        }),
      );
      await createShadowRun(workspaceId, {
        source_kind: "agenda",
        source_id: aid,
        team_recipe: { name: "manual-shadow-v0" },
        persona_contract_versions: {},
        model_versions: {},
        harness: "subscription_cli",
        budget_micros: Math.round(usd * 1_000_000),
      });
      setInfo(`Queued shadow run for agenda ${aid}.`);
      onCreated();
    } catch (e) {
      if (isPremiumRequired(e)) {
        onPremiumRequired();
      } else {
        setErr(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-edge pt-2">
      <span className="text-[0.625rem] text-muted">Queue manual shadow:</span>
      {agendas.length > 0 ? (
        <select
          value={agendaId}
          onChange={(e) => setAgendaId(e.target.value)}
          className="min-w-[12rem] rounded-md border border-edge bg-surface px-2 py-1 text-[0.625rem] text-primary"
        >
          <option value="">Pick an agenda</option>
          {agendas.map((agenda) => (
            <option key={agenda.agenda_id} value={String(agenda.agenda_id)}>
              #{agenda.agenda_id} - {agenda.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="number"
          value={agendaId}
          onChange={(e) => setAgendaId(e.target.value)}
          placeholder="agenda_id"
          className="w-24 rounded-md border border-edge bg-surface px-2 py-1 font-code text-[0.625rem] text-primary"
        />
      )}
      <input
        type="number"
        step="0.01"
        value={budget}
        onChange={(e) => setBudget(e.target.value)}
        placeholder="budget USD"
        className="w-24 rounded-md border border-edge bg-surface px-2 py-1 font-code text-[0.625rem] text-primary"
      />
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy}
        className="rounded-md border border-warning px-2 py-1 text-[0.625rem] font-medium text-warning hover:bg-warning/10 disabled:opacity-50"
      >
        {busy ? "Queueing…" : "Queue"}
      </button>
      {info && <span className="text-[0.625rem] text-success">{info}</span>}
      {err && <span className="text-[0.625rem] text-danger">{err}</span>}
    </div>
  );
}
